import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import BtnTaratic from '../../components/ui/BtnTaratic';
import { GhostInput } from '../../components/ui/GhostInput';
import { GhostSelect } from '../../components/ui/GhostSelect';
import { clinicsService, type Doctor, type DoctorWorkday, type DoctorVacation } from '../../services/clinicsService';
import { useNotifications } from '../../components/ui/notifications';

interface ClinicsDoctorModalProps {
  doctor: Doctor | null;
  onClose: () => void;
  onSaved: () => void;
}

const DAYS_OF_WEEK = [
  { value: 'monday', label: 'Lunes' },
  { value: 'tuesday', label: 'Martes' },
  { value: 'wednesday', label: 'Miércoles' },
  { value: 'thursday', label: 'Jueves' },
  { value: 'friday', label: 'Viernes' },
  { value: 'saturday', label: 'Sábado' },
  { value: 'sunday', label: 'Domingo' }
];

const ClinicsDoctorModal: React.FC<ClinicsDoctorModalProps> = ({ doctor, onClose, onSaved }) => {
  const { t } = useTranslation();
  const notifications = useNotifications();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<Partial<Doctor>>({
    name: '',
    specialty: '',
    phone: '',
    email: '',
    bio: '',
    is_active: true,
    workdays: [],
    vacations: []
  });

  useEffect(() => {
    if (doctor) {
      setFormData(doctor);
    }
  }, [doctor]);

  const handleChange = (field: keyof Doctor, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddWorkday = () => {
    setFormData(prev => ({
      ...prev,
      workdays: [...(prev.workdays || []), { day_of_week: 'monday', start_time: '09:00:00', end_time: '17:00:00' } as any]
    }));
  };

  const handleUpdateWorkday = (index: number, field: keyof DoctorWorkday, value: any) => {
    const newWorkdays = [...(formData.workdays || [])];
    newWorkdays[index] = { ...newWorkdays[index], [field]: value };
    setFormData(prev => ({ ...prev, workdays: newWorkdays }));
  };

  const handleRemoveWorkday = (index: number) => {
    const newWorkdays = [...(formData.workdays || [])];
    newWorkdays.splice(index, 1);
    setFormData(prev => ({ ...prev, workdays: newWorkdays }));
  };

  const handleAddVacation = () => {
    setFormData(prev => ({
      ...prev,
      vacations: [...(prev.vacations || []), { start_date: '', end_date: '', reason: '' } as any]
    }));
  };

  const handleUpdateVacation = (index: number, field: keyof DoctorVacation, value: any) => {
    const newVacations = [...(formData.vacations || [])];
    newVacations[index] = { ...newVacations[index], [field]: value };
    setFormData(prev => ({ ...prev, vacations: newVacations }));
  };

  const handleRemoveVacation = (index: number) => {
    const newVacations = [...(formData.vacations || [])];
    newVacations.splice(index, 1);
    setFormData(prev => ({ ...prev, vacations: newVacations }));
  };

  const handleSave = async () => {
    const name = formData.name?.trim();
    if (!name) {
      notifications.warning('Revisa los datos', 'El nombre del doctor es obligatorio.');
      return;
    }

    const vacations = (formData.vacations || []).filter(vacation =>
      Boolean(
        vacation.start_date ||
        vacation.end_date ||
        vacation.reason?.trim()
      )
    );

    if (vacations.some(vacation => !vacation.start_date || !vacation.end_date)) {
      notifications.warning('Revisa las vacaciones', 'Cada periodo debe incluir fecha de inicio y fecha de fin.');
      return;
    }

    if (vacations.some(vacation => vacation.end_date < vacation.start_date)) {
      notifications.warning('Revisa las vacaciones', 'La fecha de fin no puede ser anterior a la fecha de inicio.');
      return;
    }

    try {
      setLoading(true);
      const cleanData = {
        name,
        specialty: formData.specialty?.trim() || null,
        phone: formData.phone?.trim() || null,
        email: formData.email?.trim() || null,
        bio: formData.bio?.trim() || null,
        color: formData.color || null,
        is_active: formData.is_active ?? true,
        workdays: formData.workdays?.map(wd => ({
          day_of_week: wd.day_of_week,
          start_time: wd.start_time.length === 5 ? `${wd.start_time}:00` : wd.start_time,
          end_time: wd.end_time.length === 5 ? `${wd.end_time}:00` : wd.end_time
        })),
        vacations: vacations.map(vacation => ({
          start_date: vacation.start_date,
          end_date: vacation.end_date,
          reason: vacation.reason?.trim() || null
        }))
      };

      if (doctor) {
        await clinicsService.updateDoctor(doctor.id, cleanData);
        notifications.success('Doctor actualizado', `${name} se actualizó correctamente.`);
      } else {
        await clinicsService.createDoctor(cleanData);
        notifications.success('Doctor creado', `${name} se añadió correctamente.`);
      }
      onSaved();
    } catch (error: any) {
      console.error(error);
      const detail = error.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail.map(item => item?.msg || String(item)).join(' · ')
        : detail;
      notifications.error('No se pudo guardar el doctor', message || 'Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-ui="clinics-doctor-modal-region-001" className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-filter backdrop-blur-xl">
      <div data-ui="clinics-doctor-modal-region-002" className="bg-[#111] rounded-2xl w-full max-w-4xl max-h-full flex flex-col shadow-2xl border border-white/10">
        
        {/* Header */}
        <div data-ui="clinics-doctor-modal-region-003" className="flex items-center justify-between p-6 border-b border-white/10">
          <h2 className="text-xl font-bold text-white">
            {doctor ? 'Editar Doctor' : 'Nuevo Doctor'}
          </h2>
          <BtnTaratic
            type="button"
            onClick={onClose}
            color="ghost-dark"
            iconOnly
            data-ui="clinics-doctor-modal-close"
            className="!w-8 !h-8"
            aria-label={t('common.close', 'Cerrar')}
          >
            <FontAwesomeIcon icon={faTimes} />
          </BtnTaratic>
        </div>

        {/* Body */}
        <div data-ui="clinics-doctor-modal-region-004" className="flex-1 overflow-y-auto p-6 space-y-8">
          
          {/* General Info */}
          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-white/80 border-b border-white/10 pb-2">Información General</h3>
            <div data-ui="clinics-doctor-modal-region-005" className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="grid gap-1">
                <span className="text-xs text-white/80">Nombre completo *</span>
                <GhostInput
                  value={formData.name || ''}
                  onChange={e => handleChange('name', e.target.value)}
                  placeholder="Dr. Juan Pérez"
                />
              </label>
              <label className="grid gap-1">
                <span className="text-xs text-white/80">Especialidad</span>
                <GhostInput
                  value={formData.specialty || ''}
                  onChange={e => handleChange('specialty', e.target.value)}
                  placeholder="Cardiología"
                />
              </label>
              <label className="grid gap-1">
                <span className="text-xs text-white/80">Teléfono</span>
                <GhostInput
                  value={formData.phone || ''}
                  onChange={e => handleChange('phone', e.target.value)}
                  placeholder="+34 600 000 000"
                />
              </label>
              <label className="grid gap-1">
                <span className="text-xs text-white/80">Email</span>
                <GhostInput
                  type="email"
                  value={formData.email || ''}
                  onChange={e => handleChange('email', e.target.value)}
                  placeholder="juan@clinica.com"
                />
              </label>
            </div>
            <label className="grid gap-1">
              <span className="text-xs text-white/80">Biografía / Notas</span>
              <GhostInput
                value={formData.bio || ''}
                onChange={e => handleChange('bio', e.target.value)}
                placeholder="Información adicional sobre el doctor"
              />
            </label>
          </section>

          {/* Workdays */}
          <section className="space-y-4">
            <div data-ui="clinics-doctor-modal-region-006" className="flex items-center justify-between border-b border-white/10 pb-2">
              <h3 className="text-lg font-semibold text-white/80">Horario de Trabajo</h3>
              <button 
                type="button"
                onClick={handleAddWorkday}
                className="cursor-pointer text-sm text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-1"
              >
                <FontAwesomeIcon icon={faPlus} /> Añadir día
              </button>
            </div>
            
            {formData.workdays?.length === 0 && (
              <p className="text-white/40 text-sm italic">No se han definido días laborables.</p>
            )}

            <div data-ui="clinics-doctor-modal-region-007" className="space-y-3">
              {formData.workdays?.map((wd, idx) => (
                <div data-ui="clinics-doctor-modal-region-008" key={idx} className="flex flex-col sm:flex-row gap-3 items-end bg-white/5 p-3 rounded-xl border border-white/5">
                  <div data-ui="clinics-doctor-modal-region-009" className="flex-1 w-full">
                    <label className="grid gap-1">
                      <span className="text-xs text-white/80">Día de la semana</span>
                      <GhostSelect
                        options={DAYS_OF_WEEK}
                        value={wd.day_of_week}
                        onChange={v => handleUpdateWorkday(idx, 'day_of_week', v)}
                      />
                    </label>
                  </div>
                  <div data-ui="clinics-doctor-modal-region-010" className="w-full sm:w-32">
                    <label className="grid gap-1">
                      <span className="text-xs text-white/80">Hora inicio</span>
                      <GhostInput
                        type="time"
                        value={wd.start_time.substring(0, 5)}
                        onChange={e => handleUpdateWorkday(idx, 'start_time', e.target.value)}
                      />
                    </label>
                  </div>
                  <div data-ui="clinics-doctor-modal-region-011" className="w-full sm:w-32">
                    <label className="grid gap-1">
                      <span className="text-xs text-white/80">Hora fin</span>
                      <GhostInput
                        type="time"
                        value={wd.end_time.substring(0, 5)}
                        onChange={e => handleUpdateWorkday(idx, 'end_time', e.target.value)}
                      />
                    </label>
                  </div>
                  <button 
                    type="button"
                    onClick={() => handleRemoveWorkday(idx)}
                    className="cursor-pointer h-11 px-3 text-red-400 hover:text-red-300 bg-white/5 hover:bg-white/10 rounded-xl transition-colors"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              ))}
            </div>
          </section>

          {/* Vacations */}
          <section className="space-y-4">
            <div data-ui="clinics-doctor-modal-region-012" className="flex items-center justify-between border-b border-white/10 pb-2">
              <h3 className="text-lg font-semibold text-white/80">Vacaciones</h3>
              <button 
                type="button"
                onClick={handleAddVacation}
                className="cursor-pointer text-sm text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-1"
              >
                <FontAwesomeIcon icon={faPlus} /> Añadir periodo
              </button>
            </div>

            {formData.vacations?.length === 0 && (
              <p className="text-white/40 text-sm italic">No se han definido vacaciones.</p>
            )}

            <div data-ui="clinics-doctor-modal-region-013" className="space-y-3">
              {formData.vacations?.map((vac, idx) => (
                <div data-ui="clinics-doctor-modal-region-014" key={idx} className="flex flex-col sm:flex-row gap-3 items-end bg-white/5 p-3 rounded-xl border border-white/5">
                  <div data-ui="clinics-doctor-modal-region-015" className="w-full sm:w-40">
                    <label className="grid gap-1">
                      <span className="text-xs text-white/80">Inicio</span>
                      <GhostInput
                        type="date"
                        value={vac.start_date ? vac.start_date.substring(0, 10) : ''}
                        onChange={e => handleUpdateVacation(idx, 'start_date', e.target.value)}
                      />
                    </label>
                  </div>
                  <div data-ui="clinics-doctor-modal-region-016" className="w-full sm:w-40">
                    <label className="grid gap-1">
                      <span className="text-xs text-white/80">Fin</span>
                      <GhostInput
                        type="date"
                        value={vac.end_date ? vac.end_date.substring(0, 10) : ''}
                        onChange={e => handleUpdateVacation(idx, 'end_date', e.target.value)}
                      />
                    </label>
                  </div>
                  <div data-ui="clinics-doctor-modal-region-017" className="flex-1 w-full">
                    <label className="grid gap-1">
                      <span className="text-xs text-white/80">Motivo (opcional)</span>
                      <GhostInput
                        value={vac.reason || ''}
                        onChange={e => handleUpdateVacation(idx, 'reason', e.target.value)}
                        placeholder="Ej. Vacaciones de verano"
                      />
                    </label>
                  </div>
                  <button 
                    type="button"
                    onClick={() => handleRemoveVacation(idx)}
                    className="cursor-pointer h-11 px-3 text-red-400 hover:text-red-300 bg-white/5 hover:bg-white/10 rounded-xl transition-colors"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              ))}
            </div>
          </section>

        </div>

        {/* Footer */}
        <div data-ui="clinics-doctor-modal-region-018" className="p-6 border-t border-white/10 flex justify-end gap-3 bg-[#0a0a0a] rounded-b-2xl">
          <BtnTaratic type="button" data-ui="clinics-doctor-modal-cancel" color="ghost-dark" onClick={onClose}>
            {t('common.cancel', 'Cancelar')}
          </BtnTaratic>
          <BtnTaratic type="button" color="green" onClick={handleSave} disabled={loading}>
            {loading ? 'Guardando...' : 'Guardar Doctor'}
          </BtnTaratic>
        </div>
      </div>
    </div>
  );
};

export default ClinicsDoctorModal;
